/**
 * AdminAiTaggingPage - Run AI tagging jobs
 *
 * Drives POST /api/admin/ai-tagging/run with:
 * - dry_run=true (preview) always available
 * - dry_run=false (execute) requires explicit confirmation
 */

import { useState, useEffect } from 'react';
import { Play, Eye, AlertTriangle, CheckCircle2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

interface SelectorOverrides {
  media_types: ('movie' | 'tv')[];
  min_year: number | null;
  max_year: number | null;
  min_imdb_votes: number;
  max_imdb_votes: number | null;
  language_allow: string[] | null;
  language_deny: string[] | null;
  genre_ids: number[] | null;
}

interface Cursor {
  imdb_votes: number;
  tmdb_id: number;
  media_type: 'movie' | 'tv';
}

interface AiTaggingRequest {
  job_type: 'ai_facets_v1' | 'ai_overlay_genres_v1' | 'ai_microtag_v1';
  max_items: number;
  dry_run: boolean;
  model?: string;
  prompt_version?: string;
  selector_overrides?: Partial<SelectorOverrides>;
  cursor?: Cursor | null;
  // Microtag-specific fields
  tag_key?: string;
  tag_prompt?: string;
  force_retag?: boolean;
  min_confidence?: number;
}

interface DryRunResponse {
  dry_run: true;
  job_type: string;
  model: string;
  prompt_version: string;
  missing_token?: string; // Not present for microtags
  selector: Record<string, unknown>;
  cohort_stats: {
    movies: number;
    tv: number;
    total: number;
  };
  items_selected: number; // Total items that would be processed
  items_previewed: number; // Number of items in preview (capped at 100)
  items: Array<{
    media_type: string;
    tmdb_id: number;
    title: string;
    imdb_votes: number;
    missing_fields?: string[]; // For facets/overlay
    existing_confidence?: number | null; // For microtags
    original_language: string;
    year: number;
  }>;
  next_cursor: Cursor | null;
  // Microtag-specific fields
  tag_key?: string;
  tag_prompt?: string | null;
}

interface ExecuteResponse {
  success: boolean;
  dry_run: false;
  job_run_id: string;
  job_type: string;
  model: string;
  prompt_version: string;
  items_selected: number;
  batches_enqueued: number;
  batch_size: number;
  selector: Record<string, unknown>;
  next_cursor: Cursor | null;
  // Microtag-specific fields
  tag_key?: string;
  tag_prompt?: string | null;
}

type AiTaggingResponse = DryRunResponse | ExecuteResponse;

export function AdminAiTaggingPage() {
  const { session } = useAuth();

  // Form state
  const [jobType, setJobType] = useState<'ai_facets_v1' | 'ai_overlay_genres_v1' | 'ai_microtag_v1'>('ai_facets_v1');
  const [maxItems, setMaxItems] = useState(10);
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [promptVersion, setPromptVersion] = useState('v1');

  // Microtag-specific state
  const [tagKey, setTagKey] = useState('');
  const [tagPrompt, setTagPrompt] = useState('');
  const [forceRetag, setForceRetag] = useState(false);
  const [minConfidence, setMinConfidence] = useState<string>('');

  // Selector overrides
  const [mediaTypes, setMediaTypes] = useState<('movie' | 'tv')[]>(['movie', 'tv']);
  const [minYear, setMinYear] = useState<string>('');
  const [maxYear, setMaxYear] = useState<string>('');
  const [minImdbVotes, setMinImdbVotes] = useState(1000);
  const [maxImdbVotes, setMaxImdbVotes] = useState<string>('');
  const [genreIds, setGenreIds] = useState<number[]>([]);

  // Cursor for resume
  const [cursorJson, setCursorJson] = useState('');

  // Read cohortFilters from sessionStorage on mount (handoff from Cohort Builder)
  useEffect(() => {
    const stored = sessionStorage.getItem('cohortFilters');
    if (stored) {
      try {
        const filters = JSON.parse(stored);
        if (Array.isArray(filters.media_types)) {
          setMediaTypes(filters.media_types.filter((t: string) => t === 'movie' || t === 'tv'));
        }
        if (typeof filters.min_year === 'number') {
          setMinYear(filters.min_year.toString());
        }
        if (typeof filters.max_year === 'number') {
          setMaxYear(filters.max_year.toString());
        }
        if (typeof filters.min_imdb_votes === 'number') {
          setMinImdbVotes(filters.min_imdb_votes);
        }
        if (typeof filters.max_imdb_votes === 'number') {
          setMaxImdbVotes(filters.max_imdb_votes.toString());
        }
        if (Array.isArray(filters.genre_ids)) {
          setGenreIds(filters.genre_ids.filter((id: unknown) => typeof id === 'number'));
        }
        // Remove after applying to avoid repeated application
        sessionStorage.removeItem('cohortFilters');
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Execution state
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AiTaggingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Confirmation state for execute
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  // Advanced options toggle
  const [showAdvanced, setShowAdvanced] = useState(false);

  const buildRequest = (dryRun: boolean): AiTaggingRequest => {
    const req: AiTaggingRequest = {
      job_type: jobType,
      max_items: maxItems,
      dry_run: dryRun,
      model,
      prompt_version: promptVersion,
      selector_overrides: {
        media_types: mediaTypes,
        min_year: minYear ? parseInt(minYear, 10) : null,
        max_year: maxYear ? parseInt(maxYear, 10) : null,
        min_imdb_votes: minImdbVotes,
        max_imdb_votes: maxImdbVotes ? parseInt(maxImdbVotes, 10) : null,
        language_allow: null,
        language_deny: null,
        genre_ids: genreIds.length > 0 ? genreIds : null,
      },
    };

    // Add microtag-specific fields
    if (jobType === 'ai_microtag_v1') {
      req.tag_key = tagKey;
      if (tagPrompt.trim()) {
        req.tag_prompt = tagPrompt;
      }
      req.force_retag = forceRetag;
      if (forceRetag && minConfidence.trim()) {
        req.min_confidence = parseFloat(minConfidence);
      }
    }

    // Parse cursor if provided
    if (cursorJson.trim()) {
      try {
        req.cursor = JSON.parse(cursorJson);
      } catch {
        // Invalid JSON, ignore
      }
    }

    return req;
  };

  const runPreview = async () => {
    if (!session?.access_token) return;

    setIsLoading(true);
    setError(null);
    setResult(null);
    setShowConfirmation(false);
    setConfirmChecked(false);

    try {
      const response = await apiFetch<AiTaggingResponse>('/admin/ai-tagging/run', {
        method: 'POST',
        token: session.access_token,
        body: buildRequest(true),
      });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const runExecute = async () => {
    if (!session?.access_token || !confirmChecked) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<AiTaggingResponse>('/admin/ai-tagging/run', {
        method: 'POST',
        token: session.access_token,
        body: buildRequest(false),
      });
      setResult(response);
      setShowConfirmation(false);
      setConfirmChecked(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const isDryRunResult = (r: AiTaggingResponse): r is DryRunResponse => r.dry_run === true;

  return (
    <AdminLayout
      title="AI Tagging"
      description="Run AI tagging jobs for movies and TV shows"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-6">
          {/* Job Type */}
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Job Type
            </label>
            <select
              value={jobType}
              onChange={(e) => setJobType(e.target.value as typeof jobType)}
              className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
            >
              <option value="ai_facets_v1">AI Facets v1 (full tags)</option>
              <option value="ai_overlay_genres_v1">AI Overlay Genres v1</option>
              <option value="ai_microtag_v1">AI Microtag v1 (boolean tags)</option>
            </select>
          </div>

          {/* Microtag-specific Options */}
          {jobType === 'ai_microtag_v1' && (
            <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tag Key <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={tagKey}
                  onChange={(e) => setTagKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="is_valentines"
                  className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must match pattern: is_[a-z0-9_] (e.g., is_valentines, is_christmas)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tag Prompt (optional)
                </label>
                <textarea
                  value={tagPrompt}
                  onChange={(e) => setTagPrompt(e.target.value)}
                  placeholder="Does this title prominently feature Valentine's Day themes?"
                  rows={2}
                  className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Custom prompt for this tag. Leave empty to use default prompt for common tags.
                </p>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={forceRetag}
                    onChange={(e) => {
                      setForceRetag(e.target.checked);
                      if (!e.target.checked) setMinConfidence('');
                    }}
                    className="rounded border-white/[0.12] bg-[#0F1419] text-teal-500 focus:ring-teal-500"
                  />
                  Force Re-tag
                </label>
              </div>

              {forceRetag && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Min Confidence Threshold (optional)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={minConfidence}
                    onChange={(e) => setMinConfidence(e.target.value)}
                    placeholder="0.8"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Re-tag items with confidence below this threshold (0.0-1.0). Leave empty to re-tag all.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Basic Options */}
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Max Items (1-10,000)
              </label>
              <input
                type="number"
                min={1}
                max={10000}
                value={maxItems}
                onChange={(e) => setMaxItems(Math.min(10000, Math.max(1, parseInt(e.target.value, 10) || 10)))}
                className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Media Types
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={mediaTypes.includes('movie')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setMediaTypes([...mediaTypes, 'movie']);
                      } else {
                        setMediaTypes(mediaTypes.filter((t) => t !== 'movie'));
                      }
                    }}
                    className="rounded border-white/[0.12] bg-[#0F1419] text-teal-500 focus:ring-teal-500"
                  />
                  Movies
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={mediaTypes.includes('tv')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setMediaTypes([...mediaTypes, 'tv']);
                      } else {
                        setMediaTypes(mediaTypes.filter((t) => t !== 'tv'));
                      }
                    }}
                    className="rounded border-white/[0.12] bg-[#0F1419] text-teal-500 focus:ring-teal-500"
                  />
                  TV Shows
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Min IMDb Votes
              </label>
              <input
                type="number"
                min={0}
                value={minImdbVotes}
                onChange={(e) => setMinImdbVotes(parseInt(e.target.value, 10) || 0)}
                className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Advanced Options */}
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-between w-full text-sm font-medium text-slate-300"
            >
              Advanced Options
              {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Min Year
                    </label>
                    <input
                      type="number"
                      value={minYear}
                      onChange={(e) => setMinYear(e.target.value)}
                      placeholder="e.g., 2000"
                      className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Max Year
                    </label>
                    <input
                      type="number"
                      value={maxYear}
                      onChange={(e) => setMaxYear(e.target.value)}
                      placeholder="e.g., 2024"
                      className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Max IMDb Votes (empty = no limit)
                  </label>
                  <input
                    type="number"
                    value={maxImdbVotes}
                    onChange={(e) => setMaxImdbVotes(e.target.value)}
                    placeholder="e.g., 100000"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Model
                  </label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (recommended)</option>
                    <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Prompt Version
                  </label>
                  <input
                    type="text"
                    value={promptVersion}
                    onChange={(e) => setPromptVersion(e.target.value)}
                    placeholder="v1"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Cursor (JSON for resume)
                  </label>
                  <textarea
                    value={cursorJson}
                    onChange={(e) => setCursorJson(e.target.value)}
                    placeholder='{"imdb_votes": 50000, "tmdb_id": 12345, "media_type": "movie"}'
                    rows={2}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white font-mono text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={runPreview}
              disabled={
                isLoading ||
                mediaTypes.length === 0 ||
                (jobType === 'ai_microtag_v1' && !/^is_[a-z0-9_]{1,60}$/.test(tagKey))
              }
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1A1F26] text-white rounded-lg border border-white/[0.12] hover:border-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Eye size={18} />
              )}
              Preview (Dry Run)
            </button>

            <button
              onClick={() => setShowConfirmation(true)}
              disabled={isLoading || mediaTypes.length === 0 || !result || !isDryRunResult(result)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play size={18} />
              Execute
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle size={18} />
                <span className="font-medium">Error</span>
              </div>
              <p className="mt-2 text-sm text-slate-300">{error}</p>
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="space-y-4">
          {result && (
            <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                {isDryRunResult(result) ? (
                  <>
                    <Eye size={18} className="text-teal-400" />
                    <span className="font-medium text-white">Preview Result</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} className="text-green-400" />
                    <span className="font-medium text-white">Job Enqueued</span>
                  </>
                )}
              </div>

              {isDryRunResult(result) ? (
                <>
                  {/* Cohort Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-3 bg-[#0F1419] rounded-lg">
                      <p className="text-xs text-slate-400">Movies</p>
                      <p className="text-xl font-bold text-white">{result.cohort_stats.movies}</p>
                    </div>
                    <div className="p-3 bg-[#0F1419] rounded-lg">
                      <p className="text-xs text-slate-400">TV Shows</p>
                      <p className="text-xl font-bold text-white">{result.cohort_stats.tv}</p>
                    </div>
                    <div className="p-3 bg-[#0F1419] rounded-lg">
                      <p className="text-xs text-slate-400">Total</p>
                      <p className="text-xl font-bold text-teal-400">{result.cohort_stats.total}</p>
                    </div>
                  </div>

                  {/* Model & Prompt */}
                  <div className="text-sm text-slate-400 mb-4">
                    <p>Model: <span className="text-white">{result.model}</span></p>
                    <p>Prompt: <span className="text-white">{result.prompt_version}</span></p>
                    {result.tag_key && (
                      <p>Tag Key: <span className="text-teal-400 font-mono">{result.tag_key}</span></p>
                    )}
                  </div>

                  {/* Next Cursor */}
                  {result.next_cursor && (
                    <div className="p-3 bg-[#0F1419] rounded-lg mb-4">
                      <p className="text-xs text-slate-400 mb-1">Next Cursor (copy for resume)</p>
                      <code className="text-xs text-teal-400 break-all">
                        {JSON.stringify(result.next_cursor)}
                      </code>
                    </div>
                  )}

                  {/* Items Preview */}
                  <div className="max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-300">Selected Items</p>
                      {result.items_selected > result.items_previewed && (
                        <span className="text-xs text-amber-400">
                          Showing {result.items_previewed.toLocaleString()} of {result.items_selected.toLocaleString()} items
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {result.items.map((item, i) => (
                        <div key={i} className="p-2 bg-[#0F1419] rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-white">{item.title}</span>
                            <span className="text-xs text-slate-400">
                              {item.media_type} · {item.year}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            ID: {item.tmdb_id} · Votes: {item.imdb_votes?.toLocaleString()} · {item.original_language}
                            {result.tag_key && item.existing_confidence !== undefined && item.existing_confidence !== null && (
                              <span className="text-yellow-500"> · Existing conf: {(item.existing_confidence * 100).toFixed(0)}%</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Execute Result */}
                  <div className="space-y-3">
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <p className="text-sm text-green-400">
                        Job {result.job_run_id} enqueued successfully
                      </p>
                    </div>
                    <div className="text-sm text-slate-400 space-y-1">
                      <p>Items Selected: <span className="text-white">{result.items_selected}</span></p>
                      <p>Batches Enqueued: <span className="text-white">{result.batches_enqueued}</span></p>
                      <p>Batch Size: <span className="text-white">{result.batch_size}</span></p>
                      <p>Model: <span className="text-white">{result.model}</span></p>
                      {result.tag_key && (
                        <p>Tag Key: <span className="text-teal-400 font-mono">{result.tag_key}</span></p>
                      )}
                    </div>
                    {result.next_cursor && (
                      <div className="p-3 bg-[#0F1419] rounded-lg">
                        <p className="text-xs text-slate-400 mb-1">Next Cursor</p>
                        <code className="text-xs text-teal-400 break-all">
                          {JSON.stringify(result.next_cursor)}
                        </code>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {!result && !isLoading && (
            <div className="p-8 bg-[#1A1F26] rounded-lg border border-white/[0.08] text-center">
              <Eye size={32} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400">
                Click "Preview" to see which items will be tagged
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md p-6 bg-[#1A1F26] rounded-xl border border-white/[0.08]">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <AlertTriangle size={24} className="text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Confirm Execution</h3>
            </div>

            <p className="text-sm text-slate-300 mb-4">
              You are about to run AI tagging on{' '}
              <span className="font-bold text-white">
                {result && isDryRunResult(result) ? result.cohort_stats.total : 0} items
              </span>
              . This will incur API costs and cannot be undone.
            </p>

            <label className="flex items-start gap-3 p-3 bg-[#0F1419] rounded-lg mb-4">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-0.5 rounded border-white/[0.12] bg-[#0F1419] text-teal-500 focus:ring-teal-500"
              />
              <span className="text-sm text-slate-300">
                I understand this will run AI tagging jobs and incur API costs. I have reviewed the preview and want to proceed.
              </span>
            </label>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmChecked(false);
                }}
                className="flex-1 px-4 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runExecute}
                disabled={!confirmChecked || isLoading}
                className="flex-1 px-4 py-2 text-sm text-white bg-orange-500 rounded-lg hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Executing...' : 'Execute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
